import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';
import { useLab } from '../i18n/LabContext';

const fmtDate = ts => ts ? new Date(ts).toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

const DAY_KEYS = { '-1': 'day_all', 0: 'day_0', 1: 'day_1', 2: 'day_2', 3: 'day_3', 4: 'day_4', 5: 'day_5', 6: 'day_6' };

function Tab({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: '9px 22px', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontWeight: 600,
      background: active ? 'rgba(0,212,255,0.12)' : 'transparent',
      border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
      color: active ? 'var(--cyan)' : 'var(--text2)',
    }}>{label}</button>
  );
}

const Inp = ({ label, type, val, onChange, opts = {} }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{label}</label>
    <input type={type} value={val} onChange={e => onChange(e.target.value)} {...opts}
      style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
        color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13,
        outline: 'none', boxSizing: 'border-box' }} />
  </div>
);

function isHappyHourActive(hh) {
  const now = new Date();
  const dow = now.getDay();
  const hour = now.getHours();
  const dayMatch = hh.day_of_week === -1 || hh.day_of_week === dow;
  return dayMatch && hour >= hh.hour_from && hour < hh.hour_to;
}

// ── ABONEMENTLAR TAB ──────────────────────────────────────────────────────
function AbonementlarTab() {
  const { t } = useLang();
  const { labId } = useLab();
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', price: '', duration_days: 30, discount_pct: 0, sessions_limit: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, ur] = await Promise.all([api.get('/memberships/plans'), api.get('/memberships/users')]);
      setPlans(pr.data);
      setUsers(ur.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCreate = async () => {
    if (!form.name || !form.price) return alert(t('alertNamePrice'));
    if (!labId) return alert(t('launcherNoBranchAlert'));
    try {
      await api.post('/memberships/plans', { ...form, lab_id: labId });
      setShowForm(false);
      setForm({ name: '', price: '', duration_days: 30, discount_pct: 0, sessions_limit: 0 });
      load();
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const togglePlan = async (p) => {
    try { await api.put(`/memberships/plans/${p.id}`, { is_active: !p.is_active }); load(); }
    catch (e) { alert(t('error')); }
  };

  const deletePlan = async id => {
    if (!window.confirm(t('confirmDelete'))) return;
    try { await api.delete(`/memberships/plans/${id}`); load(); }
    catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const activeCount = users.filter(u => u.status === 'active').length;
  const monthRevenue = users.reduce((s, u) => s + Number(u.plan_price || 0), 0);

  const statusColor = s => ({ active: 'var(--green)', expired: 'var(--red)', cancelled: 'var(--orange)' })[s] || 'var(--text3)';

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 24px' }}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>💎</div>
          <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--cyan)' }}>{activeCount}</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{t('activeAbonements')}</div>
        </div>
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 24px' }}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>💰</div>
          <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--green)' }}>
            {monthRevenue.toLocaleString()} {t('currency')}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{t('monthlyAbonRevenue')}</div>
        </div>
      </div>

      {/* Plans */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)' }}>{t('plansHeader')}</div>
          {!showForm && (
            <button onClick={() => setShowForm(true)} style={{
              background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)',
              color: 'var(--green)', padding: '8px 18px', borderRadius: 9, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              {t('addPlan')}
            </button>
          )}
        </div>

        {showForm && (
          <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Inp label={t('nameLabel')} type="text" val={form.name} onChange={v => set('name', v)} opts={{ placeholder: t('nameLabel') }} />
              <Inp label={t('priceSumLabel')} type="number" val={form.price} onChange={v => set('price', v)} opts={{ placeholder: '0', min: '0' }} />
              <Inp label={t('durationDaysLabel')} type="number" val={form.duration_days} onChange={v => set('duration_days', v)} opts={{ min: '1' }} />
              <Inp label={t('discountPct')} type="number" val={form.discount_pct} onChange={v => set('discount_pct', v)} opts={{ min: '0', max: '100' }} />
              <Inp label={t('sessionLimitLabel')} type="number" val={form.sessions_limit} onChange={v => set('sessions_limit', v)} opts={{ min: '0' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowForm(false)} style={{ flex: 1, background: 'transparent',
                border: '1px solid var(--border)', color: 'var(--text2)', padding: '9px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>
                {t('cancel')}
              </button>
              <button onClick={handleCreate} style={{ flex: 2, background: 'rgba(0,255,136,0.12)',
                border: '1px solid var(--green)', color: 'var(--green)', padding: '9px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                💾 {t('save')}
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {plans.map(p => (
            <div key={p.id} style={{ background: 'var(--card2)', border: `1px solid ${p.is_active ? 'var(--cyan)' : 'var(--border)'}`,
              borderRadius: 14, padding: 18, opacity: p.is_active ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                <button onClick={() => togglePlan(p)} style={{
                  width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                  background: p.is_active ? 'var(--green)' : 'var(--border)', position: 'relative' }}>
                  <span style={{ position: 'absolute', top: 2, left: p.is_active ? 18 : 2,
                    width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                </button>
              </div>
              <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 18, fontWeight: 700, color: 'var(--cyan)', marginBottom: 6 }}>
                {Number(p.price).toLocaleString()} {t('currency')}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span>{p.duration_days} {t('dayUnit2')}</span>
                {p.discount_pct > 0 && <span style={{ color: 'var(--green)' }}>-{p.discount_pct}% {t('discountSuffix')}</span>}
                <span>{p.sessions_limit > 0 ? `${p.sessions_limit} ${t('sessionUnit')}` : t('unlimitedSessions')}</span>
              </div>
              <button onClick={() => deletePlan(p.id)} style={{ marginTop: 10, background: 'transparent',
                border: '1px solid rgba(255,59,92,0.3)', color: 'var(--red)', padding: '5px 12px',
                borderRadius: 7, cursor: 'pointer', fontSize: 11, width: '100%' }}>{t('deleteBtn2')}</button>
            </div>
          ))}
        </div>
      </div>

      {/* Active users */}
      <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 11,
          color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>{t('activeAbonementsHeader')}</div>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}>{t('loading')}</div>
        ) : users.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}>{t('noActiveAbonements')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--card)' }}>
                {[t('colUser'), t('colPlan'), t('colStartDate'), t('colEndDate'), t('colSessions'), t('colStatus')].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 16px', fontWeight: 600 }}>{u.user_name}</td>
                  <td style={{ padding: '10px 16px', color: 'var(--cyan)' }}>{u.plan_name}</td>
                  <td style={{ padding: '10px 16px', color: 'var(--text2)' }}>{fmtDate(u.starts_at)}</td>
                  <td style={{ padding: '10px 16px', color: 'var(--text2)' }}>{fmtDate(u.ends_at)}</td>
                  <td style={{ padding: '10px 16px' }}>
                    {u.sessions_used || 0}/{u.sessions_limit > 0 ? u.sessions_limit : '∞'}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600,
                      background: statusColor(u.status) + '18', color: statusColor(u.status),
                      border: `1px solid ${statusColor(u.status)}40` }}>
                      {u.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── HAPPY HOUR TAB ────────────────────────────────────────────────────────
function HappyHourTab() {
  const { t } = useLang();
  const DAY_LABELS = Object.fromEntries(Object.entries(DAY_KEYS).map(([k, v]) => [k, t(v)]));
  const [hours, setHours] = useState([]);
  const [labId, setLabId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', day_of_week: -1, hour_from: 18, hour_to: 22, discount_pct: 20 });

  const loadHours = useCallback(async (lid) => {
    if (!lid) return;
    try { const r = await api.get(`/memberships/happy-hours/${lid}`); setHours(r.data); }
    catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    api.get('/owner/floor').then(r => {
      const id = r.data?.lab_id || r.data?.id || r.data?.[0]?.lab_id;
      setLabId(id);
      loadHours(id);
    }).catch(() => {});
  }, [loadHours]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCreate = async () => {
    if (!form.name) return alert(t('alertNameRequired'));
    try {
      await api.post('/memberships/happy-hours', { ...form, lab_id: labId });
      setShowForm(false);
      setForm({ name: '', day_of_week: -1, hour_from: 18, hour_to: 22, discount_pct: 20 });
      loadHours(labId);
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const toggleHH = async hh => {
    try { await api.put(`/memberships/happy-hours/${hh.id}`, { is_active: !hh.is_active }); loadHours(labId); }
    catch (e) { alert(t('error')); }
  };

  const deleteHH = async id => {
    if (!window.confirm(t('confirmDelete'))) return;
    try { await api.delete(`/memberships/happy-hours/${id}`); loadHours(labId); }
    catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const activeHH = hours.filter(h => h.is_active && isHappyHourActive(h));

  return (
    <div>
      {activeHH.length > 0 && (
        <div style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.3)',
          borderRadius: 14, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 24 }}>🟢</span>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--green)', fontSize: 14 }}>{t('hhActiveNow')}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>
              {activeHH.map(h => `${h.name} (${h.discount_pct}%)`).join(', ')}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        {!showForm && (
          <button onClick={() => setShowForm(true)} style={{
            background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)',
            color: 'var(--green)', padding: '10px 22px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            {t('addHH')}
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 18px', fontSize: 15 }}>{t('newHHTitle')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Inp label={t('nameLabel')} type="text" val={form.name} onChange={v => set('name', v)} opts={{ placeholder: t('nameLabel') }} />
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{t('dayLabel')}</label>
              <select value={form.day_of_week} onChange={e => set('day_of_week', Number(e.target.value))}
                style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
                  color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13, outline: 'none' }}>
                {Object.entries(DAY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <Inp label={t('hourFromLabel')} type="number" val={form.hour_from} onChange={v => set('hour_from', Number(v))} opts={{ min: '0', max: '23' }} />
            <Inp label={t('hourToLabel')} type="number" val={form.hour_to} onChange={v => set('hour_to', Number(v))} opts={{ min: '0', max: '23' }} />
            <Inp label={t('discountPct')} type="number" val={form.discount_pct} onChange={v => set('discount_pct', Number(v))} opts={{ min: '0', max: '100' }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setShowForm(false)} style={{ flex: 1, background: 'transparent',
              border: '1px solid var(--border)', color: 'var(--text2)', padding: '9px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>
              {t('cancel')}
            </button>
            <button onClick={handleCreate} style={{ flex: 2, background: 'rgba(0,255,136,0.12)',
              border: '1px solid var(--green)', color: 'var(--green)', padding: '9px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
              💾 {t('save')}
            </button>
          </div>
        </div>
      )}

      <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
        {hours.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⏰</div>
            <div style={{ fontSize: 14, color: 'var(--text2)' }}>{t('noHappyHours')}</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--card)' }}>
                {[t('colName'), t('colDay'), t('colTime'), t('colDiscount'), t('colNow'), t('colEnabled'), t('colActions')].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hours.map(hh => {
                const nowActive = isHappyHourActive(hh);
                return (
                  <tr key={hh.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>{hh.name}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--text2)' }}>{DAY_LABELS[hh.day_of_week] || hh.day_of_week}</td>
                    <td style={{ padding: '12px 16px', fontFamily: 'Orbitron, sans-serif', fontSize: 12 }}>
                      {hh.hour_from}:00 — {hh.hour_to}:00
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--green)', fontWeight: 700 }}>-{hh.discount_pct}%</td>
                    <td style={{ padding: '12px 16px' }}>
                      {nowActive && hh.is_active ? (
                        <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20,
                          background: 'rgba(0,255,136,0.12)', color: 'var(--green)', border: '1px solid rgba(0,255,136,0.3)' }}>
                          🟢 {t('activeNow')}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={() => toggleHH(hh)} style={{
                        width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                        background: hh.is_active ? 'var(--green)' : 'var(--border)', position: 'relative' }}>
                        <span style={{ position: 'absolute', top: 2, left: hh.is_active ? 18 : 2,
                          width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                      </button>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={() => deleteHH(hh.id)} style={{
                        background: 'transparent', border: '1px solid rgba(255,59,92,0.4)', color: 'var(--red)',
                        padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function Memberships() {
  const { t } = useLang();
  const [tab, setTab] = useState('abonement');
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{t('membershipsPageTitle')}</h1>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>{t('membershipsSubtitle')}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
        <Tab active={tab === 'abonement'}  onClick={() => setTab('abonement')}  label={t('tabAbonement')} />
        <Tab active={tab === 'happyhour'}  onClick={() => setTab('happyhour')}  label={t('tabHappyHour')} />
      </div>
      {tab === 'abonement' && <AbonementlarTab />}
      {tab === 'happyhour' && <HappyHourTab />}
    </div>
  );
}
